import React, { Suspense } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthProvider";
import { useTheme } from "../../contexts/ThemeContext";
import Footer from "../Footer";
import ScrollToTop from "../ScrollToTop";
import { Menu } from "lucide-react";

// Loading fallback component for Suspense
const PageLoader: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-400 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

// Simple layout for public pages that need auth context
export const PublicLayout: React.FC<{
  children: React.ReactNode;
  fullWidth?: boolean;
}> = ({ children, fullWidth = false }) => {
  const { user } = useAuth();
  const { resolvedTheme, toggleTheme } = useTheme();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  return (
    <div className="h-screen w-screen bg-background flex flex-col overflow-hidden">
      <ScrollToTop />
      <header className="flex-shrink-0 w-screen bg-gray-800 dark:bg-gray-900 shadow-lg">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center group">
              <img
                src="/logo.svg"
                alt="Vitly"
                className="h-10 w-auto transition-all duration-300 group-hover:scale-105"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-6">
              <Link
                to="/how-it-works"
                className="relative text-white/90 hover:text-white font-medium transition-all duration-300 group"
              >
                How It Works
                <span className="absolute inset-x-0 bottom-0 h-0.5 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></span>
              </Link>
              <Link
                to="/features"
                className="relative text-white/90 hover:text-white font-medium transition-all duration-300 group"
              >
                Features
                <span className="absolute inset-x-0 bottom-0 h-0.5 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></span>
              </Link>
              <Link
                to="/pricing"
                className="relative text-white/90 hover:text-white font-medium transition-all duration-300 group"
              >
                Pricing
                <span className="absolute inset-x-0 bottom-0 h-0.5 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></span>
              </Link>
              <Link
                to="/about"
                className="relative text-white/90 hover:text-white font-medium transition-all duration-300 group"
              >
                About
                <span className="absolute inset-x-0 bottom-0 h-0.5 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></span>
              </Link>
              <Link
                to="/contact"
                className="relative text-white/90 hover:text-white font-medium transition-all duration-300 group"
              >
                Contact
                <span className="absolute inset-x-0 bottom-0 h-0.5 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></span>
              </Link>
            </nav>

            {/* User Menu */}
            <div className="flex items-center space-x-3">
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg hover:bg-white/10 text-white/90 hover:text-white transition-all duration-200"
                aria-label={`Switch to ${resolvedTheme === "dark" ? "light" : "dark"} mode`}
              >
                {resolvedTheme === "dark" ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                    />
                  </svg>
                )}
              </button>

              {/* Desktop Auth Buttons */}
              <div className="hidden md:flex items-center space-x-3">
                {user ? (
                  <Link
                    to="/dashboard"
                    className="bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 px-4 py-2 rounded-lg font-semibold hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-200"
                  >
                    Dashboard
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className="text-white/90 hover:text-white font-medium transition-all duration-200"
                    >
                      Sign In
                    </Link>
                    <Link
                      to="/register"
                      className="premium-cta text-white px-4 py-2 font-semibold transition-all duration-200" style={{ backgroundColor: 'var(--premium-accent-cta, #0070f3)' }}
                    >
                      Get Started
                    </Link>
                  </>
                )}
              </div>

              {/* Mobile menu button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 rounded-lg hover:bg-white/10 text-white/90 hover:text-white transition-all duration-200"
                aria-label="Toggle mobile menu"
              >
                <Menu
                  className={`w-5 h-5 transition-transform duration-200 ${isMobileMenuOpen ? "rotate-90" : ""}`}
                />
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden border-t border-gray-700 py-4">
              <nav className="flex flex-col space-y-3">
                <Link
                  to="/how-it-works"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-white/90 hover:text-white font-medium transition-all duration-200 px-2 py-1"
                >
                  How It Works
                </Link>
                <Link
                  to="/features"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-white/90 hover:text-white font-medium transition-all duration-200 px-2 py-1"
                >
                  Features
                </Link>
                <Link
                  to="/pricing"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-white/90 hover:text-white font-medium transition-all duration-200 px-2 py-1"
                >
                  Pricing
                </Link>
                <Link
                  to="/about"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-white/90 hover:text-white font-medium transition-all duration-200 px-2 py-1"
                >
                  About
                </Link>
                <Link
                  to="/contact"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-white/90 hover:text-white font-medium transition-all duration-200 px-2 py-1"
                >
                  Contact
                </Link>
                {user ? (
                  <Link
                    to="/dashboard"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="bg-white text-gray-800 px-4 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-200 text-center"
                  >
                    Dashboard
                  </Link>
                ) : (
                  <div className="flex flex-col space-y-2 pt-2 border-t border-gray-700">
                    <Link
                      to="/login"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="text-white/90 hover:text-white font-medium transition-all duration-200 px-2 py-1"
                    >
                      Sign In
                    </Link>
                    <Link
                      to="/register"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="premium-cta text-white px-4 py-2 font-semibold transition-all duration-200 text-center" style={{ backgroundColor: 'var(--premium-accent-cta, #0070f3)' }}
                    >
                      Get Started
                    </Link>
                  </div>
                )}
              </nav>
            </div>
          )}
        </div>
      </header>

      {fullWidth ? (
        <div className="flex-1 overflow-auto">
          <Suspense fallback={<PageLoader />}>{children}</Suspense>
          <Footer />
        </div>
      ) : (
        <div className="flex-1 overflow-auto">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pb-16 md:pb-0">
            <Suspense fallback={<PageLoader />}>{children}</Suspense>
          </div>
          <Footer />
        </div>
      )}
    </div>
  );
};

export default PublicLayout;
